import { combineLatest, map, of, switchMap } from "rxjs";
import { startWith } from "libs/rxjs-operators";
import { interval } from "rxjs";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { observeFilteredMessageEntriesForVirtualList } from "~/components/content-list/MessageEntry";
import { uniq } from "lodash-comms";
import { stringComparer } from "libs/comparers";
import { getPointer } from "libs/schema";

export function observeScheduledMessages(environment: ClientEnvironment) {
  const messages$ = environment.auth.currentUserId$.pipe(
    switchMap((currentUserId) => {
      if (!currentUserId) {
        return combineLatest([
          environment.recordLoader.createObserveQueryResult<"message">(),
          environment.recordLoader.createObserveQueryResult<"message">(),
        ]);
      }

      const pendingMessages$ = environment.recordLoader.observeGetPendingSentMessages(
        { currentUserId },
        { fetchStrategy: "cache" },
      );

      const scheduledMessages$ = interval(120_000).pipe(
        startWith(() => null),
        switchMap(() => {
          const observable = environment.recordLoader.observeGetSentMessages({
            userId: currentUserId,
            sentAfter: new Date().toISOString(),
          });

          return observeFilteredMessageEntriesForVirtualList(environment, observable);
        }),
      );

      return combineLatest([pendingMessages$, scheduledMessages$]);
    }),
  );

  const currentTimestamp$ = interval(20_000).pipe(
    startWith(() => null),
    map(() => new Date().toISOString()),
  );

  return combineLatest([messages$, currentTimestamp$]).pipe(
    switchMap(([[[pendingMessages, pendingMeta], [scheduledMessages, scheduledMeta]], currentTimestamp]) => {
      const messageIds = uniq(
        [...pendingMessages, ...scheduledMessages]
          .filter(
            (m) =>
              m.sent_at > currentTimestamp ||
              !m.server_updated_at ||
              (!!m.last_edited_at && m.last_edited_at > m.server_updated_at),
          )
          .sort(
            (a, b) =>
              stringComparer(a.sent_at, b.sent_at) ||
              stringComparer(a.scheduled_to_be_sent_at, b.scheduled_to_be_sent_at) ||
              stringComparer(a.id, b.id),
          )
          .map((m) => m.id),
      );

      const isLoading = pendingMeta.isLoading || scheduledMeta.isLoading;

      return environment.recordLoader.observeGetRecords(
        messageIds.map((id) => getPointer("message", id)),
        {
          isLoading,
          fetchStrategy: "cache",
        },
      );
    }),
  );
}
